import {HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';

export type EnrichDiagnosticKind = 'http' | 'non-http';
export type EnrichDiagnosticStatusClass = '4xx' | '5xx' | 'network' | 'unknown';

export interface EnrichDiagnosticMetadata {
  kind: EnrichDiagnosticKind;
  status?: number;
  statusClass?: EnrichDiagnosticStatusClass;
}

@Injectable({
  providedIn: 'root'
})
export class EnrichConsoleDiagnosticsService {
  reportError(operation: string, error: unknown): void {
    console.error(operation, this.buildMetadata(error));
  }

  buildMetadata(error: unknown): EnrichDiagnosticMetadata {
    const status = this.getStatus(error);
    const statusClass = status === undefined ? undefined : this.getStatusClass(status);

    if (status === undefined) {
      return {kind: this.getKind(error)};
    }

    return {kind: this.getKind(error), status, statusClass};
  }

  private getKind(error: unknown): EnrichDiagnosticKind {
    if (error instanceof HttpErrorResponse) {
      return 'http';
    }

    if (typeof error === 'object' && error && 'status' in error) {
      const status = this.getFiniteStatus((error as {status: unknown}).status);
      return status === undefined ? 'non-http' : 'http';
    }

    return 'non-http';
  }

  private getStatus(error: unknown): number | undefined {
    const status = this.getFiniteStatus(this.getNestedStatus(error));
    return status;
  }

  private getNestedStatus(error: unknown): unknown {
    if (error instanceof HttpErrorResponse) {
      return error.status;
    }

    if (error && typeof error === 'object') {
      const asRecord = error as Record<string, unknown>;
      if ('status' in asRecord) {
        return asRecord['status'];
      }
      if ('statusCode' in asRecord) {
        return asRecord['statusCode'];
      }
    }

    return undefined;
  }

  private getFiniteStatus(value: unknown): number | undefined {
    if (typeof value !== 'number' || !Number.isInteger(value) || !Number.isFinite(value)) {
      return undefined;
    }
    if (value < 0 || value > 599) {
      return undefined;
    }
    return value;
  }

  private getStatusClass(status: number): EnrichDiagnosticStatusClass {
    if (status === 0) return 'network';
    if (status >= 400 && status < 500) return '4xx';
    if (status >= 500 && status < 600) return '5xx';
    return 'unknown';
  }
}
